import React, { useCallback, useState, useEffect, useRef } from "react";
import { IconButton, SwipeableDrawer } from "@mui/material";
import { Menu } from "lucide-react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { io } from "socket.io-client";
import DoctorFooter from "../doctor_dashboard/DoctorFooter";
import DoctorHeader from "../doctor_dashboard/DoctorHeader";
import PatientInfoPanel from "../doctor_dashboard/PatientInfoPanel";
import ChatBubble from "../doctor_dashboard/ChatBubble";
import DoctorSidebar, { DoctorSidebarDrawer } from "../doctor_dashboard/DoctorSidebar";
import {
  getAdminPatientsCache,
  updateAdminPatientInCache,
} from "../../cache/adminPatientsCache";
import { notifyDoctorUnreadMessagesChanged } from "../../hooks/useDoctorUnreadMessages";

const socket = io(process.env.REACT_APP_BACKEND_URL, {
  reconnection: true,
  reconnectionAttempts: 5,
  reconnectionDelay: 3000,
  auth: {
    token: localStorage.getItem("token"),
  },
});

const AdminChatPage = () => {

  const { client_id } = useParams();
  const cachedPatient = getAdminPatientsCache()?.patients?.find(
    (item) => item?._id === client_id
  );

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [infoDrawerOpen, setInfoDrawerOpen] = useState(false);

  const [messages, setMessages] = useState(cachedPatient?.messages || []);
  const [patient, setPatient] = useState(cachedPatient || null);

  const bottomRef = useRef(null);

  /* ---------------- FETCH CLIENT ---------------- */

  const fetchClient = useCallback(async () => {
      try {

        const res = await axios.get(
          process.env.REACT_APP_BACKEND_URL + `/clients/${client_id}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token") || null}`,
            },
          }
        );

        if (res.data.success) {
          const nextClient = res.data.client;

          setPatient(nextClient);
          setMessages(nextClient.messages || []);
          updateAdminPatientInCache(client_id, () => nextClient);

        }

      } catch (err) {
        console.error(err);
      }
    }, [client_id]);

  const markPatientMessagesRead = useCallback(async () => {
    if (!client_id) return;

    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/clients/${client_id}/read-all`,
        null,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || null}`,
          },
        }
      );

      setMessages((prev) =>
        prev.map((item) =>
          item?.sender === "client" ? { ...item, read: true } : item
        )
      );
      setPatient((prev) => {
        if (!prev) return prev;

        const nextPatient = {
          ...prev,
          messages: Array.isArray(prev.messages)
            ? prev.messages.map((item) =>
                item?.sender === "client" ? { ...item, read: true } : item
              )
            : [],
        };

        updateAdminPatientInCache(client_id, () => nextPatient);
        return nextPatient;
      });
      notifyDoctorUnreadMessagesChanged();
    } catch (err) {
      console.error("Failed to mark admin patient messages as read", err);
    }
  }, [client_id]);

  useEffect(() => {

    fetchClient();
    markPatientMessagesRead();

  }, [fetchClient, markPatientMessagesRead]);

  useEffect(() => {
    socket.emit("join_room", client_id);

    const handleNewMessage = (msg) => {
      if (msg.sender_id !== client_id) return;

      const nextMessage = {
        ...msg,
        read: msg.sender === "client" ? true : msg.read,
      };

      setMessages((prev) => [...prev, nextMessage]);
      setPatient((prev) => {
        if (!prev) return prev;

        const nextPatient = {
          ...prev,
          messages: [...(prev.messages || []), nextMessage],
        };

        updateAdminPatientInCache(client_id, () => nextPatient);
        return nextPatient;
      });

      if (msg.sender === "client") {
        markPatientMessagesRead();
      }
    };

    socket.on("new_message", handleNewMessage);

    return () => {
      socket.off("new_message", handleNewMessage);
    };
  }, [client_id, markPatientMessagesRead]);


  /* ---------------- SCROLL TO BOTTOM ---------------- */

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);


  if (!patient) {
    return (
      <div className="flex min-h-screen flex-col bg-gray-50">
        <DoctorHeader />
        <div className="flex flex-1 items-center justify-center bg-white text-sm text-slate-500">
          Loading patient...
        </div>
      </div>
    );
  }


  return (

    <div className="min-h-screen flex flex-col bg-gray-50">

      <DoctorHeader />

      <div className="flex flex-1 overflow-hidden">

        {/* Desktop Sidebar */}

        <div className="hidden lg:flex">
          <DoctorSidebar activeTab="Admin" setActiveTab={() => {}} />
        </div>


        {/* Mobile Sidebar */}

        <DoctorSidebarDrawer
          open={drawerOpen}
          onOpen={() => setDrawerOpen(true)}
          onClose={() => setDrawerOpen(false)}
          activeTab="Admin"
          setActiveTab={() => setDrawerOpen(false)}
        />


        {/* Chat Area */}

        <div className="flex flex-col flex-1 bg-white">


          {/* Mobile Top Bar */}

          <div className="flex items-center justify-between px-4 py-3 lg:hidden border-b">

            <div className="flex items-center gap-2">

              <IconButton onClick={() => setDrawerOpen(true)}>
                <Menu size={18} />
              </IconButton>

              <div className="flex flex-col leading-tight">

                <span className="text-sm font-semibold text-[#1E0E62]">
                  {patient.name}
                </span>

                <span className="text-[11px] text-gray-500">
                  Patient Conversation
                </span>

              </div>

            </div>

          </div>


          {/* Messages */}

          <div
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 lg:max-h-[100vh] max-h-[100vh]"
            style={{
              backgroundColor: "#F7FAFC",
              backgroundImage:
                "radial-gradient(circle, rgba(200,220,230,0.3) 1px, transparent 1px)",
              backgroundSize: "30px 30px",
            }}
          >

            {messages.map((m, i) => (
              <ChatBubble key={i} message={m} />
            ))}

            <div ref={bottomRef} />

          </div>

        </div>


        {/* Patient Info Panel */}

        <aside className="hidden lg:flex border-l w-80 bg-white">

          <PatientInfoPanel
            setInfoDrawerOpen={setInfoDrawerOpen}
            patient={patient}
          />

        </aside>


        <SwipeableDrawer
          anchor="right"
          open={infoDrawerOpen}
          onOpen={() => setInfoDrawerOpen(true)}
          onClose={() => setInfoDrawerOpen(false)}
          disableSwipeToOpen={false}
          swipeAreaWidth={36}
          ModalProps={{ keepMounted: true }}
        >

          <PatientInfoPanel
            setInfoDrawerOpen={setInfoDrawerOpen}
            patient={patient}
          />

        </SwipeableDrawer>

      </div>


    </div>
  );
};

export default AdminChatPage;
